/**
 * 요양시설 월별임금 — 필터된 직원 일괄 저장
 * ★ 지급 합계는 유지하고, 생산직 연·야·휴는 방문요양과 같이 비과세 재분리
 * ★ 과세 변동에 맞춰 4대보험·공제를 당월 요율로 다시 계산 (수동 플래그 유지)
 */
import { commonWageApi } from '@/services/api';
import type { CommonWageListRow, CommonWageTaxRate } from '@/types/commonWage';
import {
  taxRateInfoToRates,
  computeCommonWageDeductionsFromTaxable,
  type IncomeTaxRow,
  type CommonWageTaxRates,
} from '@/composables/wage/useCommonWageCalc';
import {
  rowToFormStrings,
  rowToSettings,
  rowToDeductNumbers,
  buildSavePayload,
  rowToCalcSnapshot,
  parseManualFlagsFromRow,
  isCommonWageManualFlagOn,
} from '@/utils/commonWageMapper';
import {
  applyCommonWageAccountingLunchFill,
  applyCommonWageAccountingNontax,
  applyWageOnesTruncateToPayParts,
  calcCommonWageTaxableFromParts,
  getCommonWageFacilityLunch,
  getCommonWageLedgerGrossPay,
} from '@/policies/commonWageSettingPolicy';
import { normalizeEmpName, resolveAuthoritativeBirth } from '@/utils/empMatchKey';

export interface BulkSaveOptions {
  rows: CommonWageListRow[];
  mCode: string;
  bizGubun: string;
  year: number;
  month: number;
  taxRateInfo: CommonWageTaxRate;
  incomeTaxTable: IncomeTaxRow[];
  minHourlyWage?: number;
  /** 근무일수가 비어 있을 때 넣을 달력 영업일 */
  defaultWorkDays?: number | null;
  onProgress?: (current: number, total: number) => void;
}

export interface BulkSaveResult {
  successCount: number;
  errorCount: number;
  skippedCount: number;
  errors: string[];
}

export function preparePayloadForRow(
  row: CommonWageListRow,
  opts: Pick<
    BulkSaveOptions,
    | 'mCode'
    | 'bizGubun'
    | 'year'
    | 'month'
    | 'taxRateInfo'
    | 'incomeTaxTable'
    | 'minHourlyWage'
    | 'defaultWorkDays'
  >
) {
  return prepareDeductionRecalcPayload(row, opts);
}

export async function bulkSaveCommonWageRows(options: BulkSaveOptions): Promise<BulkSaveResult> {
  const items = options.rows.filter((r) => normalizeEmpName(r.cwhd_name || r.ce_name));
  let successCount = 0;
  let errorCount = 0;
  let skippedCount = 0;
  const errors: string[] = [];

  for (let i = 0; i < items.length; i++) {
    const row = items[i];
    if (!row.cwhd_idx) {
      skippedCount++;
      options.onProgress?.(i + 1, items.length);
      continue;
    }
    try {
      const payload = preparePayloadForRow(row, options);
      const res = await commonWageApi.save(payload);
      if (!res.data?.success) {
        throw new Error(res.data?.message || '저장 실패');
      }
      successCount++;
    } catch (error: any) {
      errorCount++;
      errors.push(`${row.ce_name}: ${error.response?.data?.message || error.message || '알 수 없는 오류'}`);
    }
    options.onProgress?.(i + 1, items.length);
  }

  return { successCount, errorCount, skippedCount, errors };
}

/** 전월복사 직후 — 생산직 비과세 재분리 + 4대보험·공제 당월 요율 */
export function needsCopiedDeductionRecalc(row: CommonWageListRow): boolean {
  if (!row.cwhd_idx) return false;
  if (getCommonWageLedgerGrossPay(row) <= 0) return false;
  if (row.cwhd_is_owner === 1) return false;
  return true;
}

/**
 * 회계처리용 대장 행
 * ★ 중식 실비변상 ON → 중식비 한도 채움 표시(기본급에서 이동, 지급합계 유지)
 *    — 중식비(비과세) 칸에 한도액이 보이도록 함
 * ★ 4대보험·공제는 채움 후 과세임금으로 재산출
 * ★ 공제 수동고정 체크(cwhd_manual_flags)된 항목은 저장값 유지
 * ★ 식비공제(l_sik)는 시설 입력 중식비 기준 유지(미고정 시)
 * ★ 퇴직금은 총 지급금액에서 한도까지 채운 회계 중식비를 빼고 ÷12
 * ★ DB 저장(모두저장)은 form/스냅샷이 시설 입력 원본을 쓰므로 기본급 DB는 안 깎임
 */
export function buildCommonWageAccountingReportRow<T extends CommonWageListRow>(
  row: T,
  opts: {
    taxRates: CommonWageTaxRates;
    incomeTaxTable: IncomeTaxRow[];
    year: number;
    month: number;
  }
): T {
  const payDisplay = applyCommonWageAccountingNontax(row);
  // 화면 선택 연·월로 한도 산출 (31일 20만 / 30일 198,500 / 2월 197,000)
  // ★ 기존 저장분: DB 과세·idx 유지 → 중식 채움액을 재산출하지 않음
  const ym = { year: opts.year, month: opts.month };
  const filled = applyCommonWageAccountingLunchFill(
    {
      ...payDisplay,
      cwhd_idx: row.cwhd_idx,
      cwhd_taxable: row.cwhd_taxable,
    },
    ym
  );
  const wageTotal = getCommonWageLedgerGrossPay(filled);
  const taxableForDeduct = calcCommonWageTaxableFromParts(filled);
  const manualFlags = parseManualFlagsFromRow(row.cwhd_manual_flags);
  const settings = {
    ...rowToSettings(payDisplay),
    manualFlags,
    deductForm: rowToDeductNumbers(row),
  };
  let deduct = computeCommonWageDeductionsFromTaxable({
    wageTotal,
    taxable: taxableForDeduct,
    lunch: getCommonWageFacilityLunch(row),
    retirementLunch: Number(filled.cwhd_lunch) || 0,
    senior: Number(row.cwhd_senior) || 0,
    sunim: Number(row.cwhd_sunim) || 0,
    workHours: Number(row.cwhd_work_hours) || 0,
    settings,
    taxRates: opts.taxRates,
    incomeTaxTable: opts.incomeTaxTable,
    birthDate: resolveAuthoritativeBirth(row),
    year: opts.year,
    month: opts.month,
  });

  // 이중 안전: 수동고정 항목은 DB 저장 공제액으로 덮어씀
  const n = (key: keyof CommonWageListRow) => Number(row[key]) || 0;
  deduct = { ...deduct };
  if (isCommonWageManualFlagOn(manualFlags, 'yeon')) {
    deduct.cwhd_l_yeon = n('cwhd_l_yeon');
    deduct.cwhd_c_yeon = n('cwhd_c_yeon');
  }
  if (isCommonWageManualFlagOn(manualFlags, 'gun')) {
    deduct.cwhd_l_gun = n('cwhd_l_gun');
    deduct.cwhd_c_gun = n('cwhd_c_gun');
  }
  if (isCommonWageManualFlagOn(manualFlags, 'jang')) {
    deduct.cwhd_l_jang = n('cwhd_l_jang');
    deduct.cwhd_c_jang = n('cwhd_c_jang');
  }
  if (isCommonWageManualFlagOn(manualFlags, 'l_go')) deduct.cwhd_l_go = n('cwhd_l_go');
  if (isCommonWageManualFlagOn(manualFlags, 'c_go')) deduct.cwhd_c_go = n('cwhd_c_go');
  if (isCommonWageManualFlagOn(manualFlags, 'l_so')) deduct.cwhd_l_so = n('cwhd_l_so');
  if (isCommonWageManualFlagOn(manualFlags, 'l_ji')) deduct.cwhd_l_ji = n('cwhd_l_ji');
  if (isCommonWageManualFlagOn(manualFlags, 'l_jiwon')) deduct.cwhd_l_jiwon = n('cwhd_l_jiwon');
  if (isCommonWageManualFlagOn(manualFlags, 'l_sik')) deduct.cwhd_l_sik = n('cwhd_l_sik');
  if (isCommonWageManualFlagOn(manualFlags, 'c_sil')) deduct.cwhd_c_sil = n('cwhd_c_sil');
  if (isCommonWageManualFlagOn(manualFlags, 'c_san')) deduct.cwhd_c_san = n('cwhd_c_san');
  if (isCommonWageManualFlagOn(manualFlags, 'c_out')) deduct.cwhd_c_out = n('cwhd_c_out');
  // l_etc / l_yearend 은 항상 저장값(자동 산출 없음)
  deduct.cwhd_l_etc = n('cwhd_l_etc');
  deduct.cwhd_l_yearend = n('cwhd_l_yearend');
  deduct.cwhd_l_total =
    deduct.cwhd_l_yeon +
    deduct.cwhd_l_gun +
    deduct.cwhd_l_jang +
    deduct.cwhd_l_go +
    deduct.cwhd_l_so +
    deduct.cwhd_l_ji +
    deduct.cwhd_l_jiwon +
    deduct.cwhd_l_sik +
    deduct.cwhd_l_etc +
    deduct.cwhd_l_yearend;
  deduct.cwhd_c_total =
    deduct.cwhd_c_yeon +
    deduct.cwhd_c_gun +
    deduct.cwhd_c_jang +
    deduct.cwhd_c_go +
    deduct.cwhd_c_sil +
    deduct.cwhd_c_san +
    deduct.cwhd_c_out;

  return {
    ...filled,
    cwhd_wage_total: wageTotal,
    cwhd_taxable: taxableForDeduct,
    cwhd_l_yeon: deduct.cwhd_l_yeon,
    cwhd_l_gun: deduct.cwhd_l_gun,
    cwhd_l_jang: deduct.cwhd_l_jang,
    cwhd_l_go: deduct.cwhd_l_go,
    cwhd_l_so: deduct.cwhd_l_so,
    cwhd_l_ji: deduct.cwhd_l_ji,
    cwhd_l_jiwon: deduct.cwhd_l_jiwon,
    cwhd_l_sik: deduct.cwhd_l_sik,
    cwhd_l_etc: deduct.cwhd_l_etc,
    cwhd_l_yearend: deduct.cwhd_l_yearend,
    cwhd_l_total: deduct.cwhd_l_total,
    cwhd_c_yeon: deduct.cwhd_c_yeon,
    cwhd_c_gun: deduct.cwhd_c_gun,
    cwhd_c_jang: deduct.cwhd_c_jang,
    cwhd_c_go: deduct.cwhd_c_go,
    cwhd_c_sil: deduct.cwhd_c_sil,
    cwhd_c_san: deduct.cwhd_c_san,
    cwhd_c_out: deduct.cwhd_c_out,
    cwhd_c_total: deduct.cwhd_c_total,
  };
}

export function prepareDeductionRecalcPayload(
  row: CommonWageListRow,
  opts: Pick<
    BulkSaveOptions,
    | 'mCode'
    | 'bizGubun'
    | 'year'
    | 'month'
    | 'taxRateInfo'
    | 'incomeTaxTable'
    | 'minHourlyWage'
    | 'defaultWorkDays'
  >
) {
  if (!row.cwhd_idx) {
    throw new Error('저장된 임금이 없습니다.');
  }

  const truncated = applyWageOnesTruncateToPayParts(row, opts.year, opts.month);
  const nontaxed = applyCommonWageAccountingNontax(truncated);
  const accounting = buildCommonWageAccountingReportRow(truncated, {
    taxRates: taxRateInfoToRates(opts.taxRateInfo),
    incomeTaxTable: opts.incomeTaxTable,
    year: opts.year,
    month: opts.month,
  });
  const form = rowToFormStrings(
    row,
    opts.minHourlyWage ?? opts.taxRateInfo.minHourlyWage,
    opts.defaultWorkDays != null ? { defaultWorkDays: opts.defaultWorkDays } : undefined
  );
  const manualFlags = parseManualFlagsFromRow(row.cwhd_manual_flags);
  const settings = {
    ...rowToSettings(nontaxed),
    cwhd_production_nontax: nontaxed.cwhd_production_nontax === 1,
    manualFlags,
    deductForm: rowToDeductNumbers(row),
  };
  const snapshot = rowToCalcSnapshot(nontaxed);
  const wageTotal = getCommonWageLedgerGrossPay(nontaxed) || snapshot.cwhd_wage_total;
  const taxable = Number(accounting.cwhd_taxable) || snapshot.cwhd_taxable;

  return buildSavePayload({
    meta: {
      m_code: opts.mCode,
      cwhd_name: normalizeEmpName(row.cwhd_name || row.ce_name),
      cwhd_birth: resolveAuthoritativeBirth(row),
      cwhd_ym: `${opts.year}${String(opts.month).padStart(2, '0')}`,
      cwhd_biz_gubun: opts.bizGubun,
    },
    calc: {
      ...snapshot,
      cwhd_wage_total: wageTotal,
      cwhd_taxable: taxable,
      cwhd_l_yeon: accounting.cwhd_l_yeon ?? 0,
      cwhd_l_gun: accounting.cwhd_l_gun ?? 0,
      cwhd_l_jang: accounting.cwhd_l_jang ?? 0,
      cwhd_l_go: accounting.cwhd_l_go ?? 0,
      cwhd_l_so: accounting.cwhd_l_so ?? 0,
      cwhd_l_ji: accounting.cwhd_l_ji ?? 0,
      cwhd_l_jiwon: accounting.cwhd_l_jiwon ?? 0,
      cwhd_l_sik: accounting.cwhd_l_sik ?? 0,
      cwhd_l_etc: accounting.cwhd_l_etc ?? 0,
      cwhd_l_yearend: accounting.cwhd_l_yearend ?? 0,
      cwhd_l_total: accounting.cwhd_l_total ?? 0,
      cwhd_c_yeon: accounting.cwhd_c_yeon ?? 0,
      cwhd_c_gun: accounting.cwhd_c_gun ?? 0,
      cwhd_c_jang: accounting.cwhd_c_jang ?? 0,
      cwhd_c_go: accounting.cwhd_c_go ?? 0,
      cwhd_c_sil: accounting.cwhd_c_sil ?? 0,
      cwhd_c_san: accounting.cwhd_c_san ?? 0,
      cwhd_c_out: accounting.cwhd_c_out ?? 0,
      cwhd_c_total: accounting.cwhd_c_total ?? 0,
      netPay: Math.max(0, wageTotal - (Number(accounting.cwhd_l_total) || 0)),
    },
    form,
    settings,
    manualFlags,
    // 표 편집·일괄저장으로 재저장해도 기본급 산출 방식 유지
    basicCalcMode: row.cwhd_basic_calc_mode,
  });
}

export async function bulkRecalcCopiedDeductions(options: BulkSaveOptions): Promise<BulkSaveResult> {
  const items = options.rows.filter((r) => normalizeEmpName(r.cwhd_name || r.ce_name));
  let successCount = 0;
  let errorCount = 0;
  let skippedCount = 0;
  const errors: string[] = [];

  for (let i = 0; i < items.length; i++) {
    const row = items[i];
    if (!row.cwhd_idx) {
      skippedCount++;
      options.onProgress?.(i + 1, items.length);
      continue;
    }
    try {
      const payload = prepareDeductionRecalcPayload(row, options);
      const res = await commonWageApi.save(payload);
      if (!res.data?.success) {
        throw new Error(res.data?.message || '저장 실패');
      }
      successCount++;
    } catch (error: any) {
      errorCount++;
      errors.push(`${row.ce_name}: ${error.response?.data?.message || error.message || '알 수 없는 오류'}`);
    }
    options.onProgress?.(i + 1, items.length);
  }

  return { successCount, errorCount, skippedCount, errors };
}
